/**
 * NetworkGraphViz - Gene regulatory network visualization for chat mode.
 *
 * Displays interactive D3 force-directed graph showing gene-gene correlations,
 * protein-protein interactions, and FDA-approved drug targets.
 *
 * Supports disease/normal network comparison and enrichment metrics display.
 */

import React, { useState, useRef, useEffect } from 'react';
import NetworkGraph from '../../NetworkGraph';  // Use same component as Pro mode
import InfoTooltip from './InfoTooltip';

interface NetworkGraphVizProps {
  data: {
    type: 'network';
    disease_grn?: {
      nodes: any[];
      edges: any[];
      n_nodes: number;
      n_edges: number;
    };
    normal_grn?: {
      nodes: any[];
      edges: any[];
      n_nodes: number;
      n_edges: number;
    };
    comparison?: {
      disease_only_edges?: any[];
      normal_only_edges?: any[];
      shared_edges?: any[];
    };
    nodes?: any[];  // Legacy support
    edges?: any[];  // Legacy support
    statistics: {
      total_nodes: number;
      total_edges: number;
      lncrna_count: number;
      avg_degree: number;
      hub_genes?: string[];
      fda_targets?: number;
      enrichment_score?: number;
      metrics?: {
        delta_edges?: number;
        delta_protein_coding?: number;
        specificity_ratio?: number;
      };
    };
    metadata?: {
      lncrna: string;
      disease: string;
    };
  };
  interactive?: boolean;
  onAction?: (action: string, params?: any) => void;
}

export const NetworkGraphViz: React.FC<NetworkGraphVizProps> = ({
  data,
  interactive = true,
  onAction
}) => {
  const [viewMode, setViewMode] = useState<'disease' | 'normal' | 'comparison'>('disease');
  const [showEnrichmentInfo, setShowEnrichmentInfo] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showEnrichmentInfo) return;
    const handler = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowEnrichmentInfo(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEnrichmentInfo]);

  const handleNodeClick = (nodeId: string) => {
    if (onAction && interactive) {
      onAction('view_gene_details', { gene: nodeId });
    }
  };

  // Support both new structured format and legacy format
  const diseaseNetwork = data.disease_grn || {
    nodes: data.nodes || [],
    edges: data.edges || [],
    n_nodes: data.nodes?.length || 0,
    n_edges: data.edges?.length || 0
  };

  // Build statistics from available fields — precomputed GRNs store counts inside disease_grn
  const dataAny = data as any;
  const statistics = data.statistics ?? {
    total_nodes: diseaseNetwork.nodes?.length ?? 0,
    total_edges: diseaseNetwork.edges?.length ?? 0,
    enrichment_score: dataAny.enrichment_score,
    metrics: dataAny.metrics,
    hub_genes: [],
    fda_targets: 0,
  };

  const normalNetwork = data.normal_grn;
  const hasNormalNetwork = normalNetwork && normalNetwork.nodes && normalNetwork.nodes.length > 0;

  // Determine which network to display
  const displayNetwork = viewMode === 'normal' && hasNormalNetwork
    ? normalNetwork
    : diseaseNetwork;

  // Prepare comparison data with required fields
  const comparisonData = data.comparison &&
    data.comparison.disease_only_edges &&
    data.comparison.normal_only_edges
    ? {
        disease_only_edges: data.comparison.disease_only_edges,
        normal_only_edges: data.comparison.normal_only_edges
      }
    : undefined;

  return (
    <div className="origin-card mt-4 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">
          Gene Regulatory Network
          {data.metadata?.lncrna && ` - ${data.metadata.lncrna}`}
        </h3>

        {/* View Mode Toggle */}
        {hasNormalNetwork && (
          <div className="flex gap-2 mb-4 border-b pb-2">
            <button
              onClick={() => setViewMode('disease')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                viewMode === 'disease'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Disease Network
            </button>
            <button
              onClick={() => setViewMode('normal')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                viewMode === 'normal'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Normal Network
            </button>
            {comparisonData && (
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  viewMode === 'comparison'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Comparison
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
          <span className="flex items-center gap-1">
            <strong>{statistics.total_nodes}</strong> genes
            <InfoTooltip title="Genes" content="Number of unique genes in the regulatory network, including the target lncRNA and its correlated protein-coding partners." />
          </span>
          <span className="flex items-center gap-1">
            <strong>{statistics.total_edges}</strong> interactions
            <InfoTooltip title="Interactions" content="Number of regulatory edges (gene-gene correlations above threshold). Higher = more connected network." />
          </span>
          {statistics.fda_targets !== undefined && statistics.fda_targets > 0 && (
            <span className="flex items-center gap-1 text-blue-400">
              <strong>{statistics.fda_targets}</strong> FDA targets
              <InfoTooltip title="FDA Targets" content="Number of genes in this network that have existing FDA-approved drugs. Indicates drug repurposing opportunities." />
            </span>
          )}
        </div>

        {statistics.hub_genes && statistics.hub_genes.length > 0 && (
          <div className="mt-2 text-sm">
            <span className="text-gray-400">Hub genes: </span>
            <span className="font-mono text-xs">
              {statistics.hub_genes.slice(0, 5).join(', ')}
            </span>
          </div>
        )}

        {/* Enrichment Score (if available) */}
        {statistics.enrichment_score !== undefined && statistics.enrichment_score !== null && (
          <div className="mt-4 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg border border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-300">
                Network Enrichment Quality
              </h4>
              <div className="relative" ref={infoRef}>
                <button
                  onClick={() => setShowEnrichmentInfo(v => !v)}
                  className="w-4 h-4 rounded-full bg-gray-600 text-gray-300 text-xs flex items-center justify-center hover:bg-blue-500 hover:text-white transition-colors leading-none"
                  title="What is this?"
                >
                  i
                </button>
                {showEnrichmentInfo && (
                  <div className="absolute left-0 top-6 z-20 w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-3 text-xs text-gray-300">
                    <p className="font-semibold mb-1">Network Enrichment Quality</p>
                    <p className="mb-2">A score from 0–1 measuring how disease-specific this lncRNA's regulatory network is compared to a matched normal tissue network. Higher = more disease-specific rewiring.</p>
                    <p className="font-semibold mb-1">Specificity Ratio</p>
                    <p>The fraction of disease network edges that are absent in the normal network — edges unique to the disease context. A higher ratio means the lncRNA drives regulation that is specific to disease, not baseline biology.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-blue-400">
                {statistics.enrichment_score.toFixed(3)}
              </span>
              <span className="text-xs text-gray-400">/ 1.000</span>
            </div>

            {statistics.metrics?.specificity_ratio !== undefined && statistics.metrics.specificity_ratio !== null && (
              <div className="mt-2 text-xs">
                <span className="text-gray-400">Specificity Ratio:</span>
                <span className="font-mono font-semibold ml-1">
                  {statistics.metrics.specificity_ratio.toFixed(2)}x
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <NetworkGraph
          data={displayNetwork}
        />
      </div>

      {data.metadata?.disease && (
        <div className="mt-2 text-xs text-gray-500">
          Context: {data.metadata.disease}
        </div>
      )}
    </div>
  );
};

export default NetworkGraphViz;
